/**
 * SmartAds Header JS (Dawn-safe)
 * - Sticky shadow toggle (no CLS)
 * - Announcement rotate (optional)
 * - Drawer body lock via <details> open state (compatible with Dawn header-drawer)
 */

// Ensure <html> has .js (needed for overlay pseudo-element selectors)
document.documentElement.classList.add('js');

class SAHeader extends HTMLElement {
  connectedCallback() {
    this.wrap = this.querySelector('.sa-header__wrap');
    this.isSticky = this.dataset.sticky === 'true';
    this.announcementEnabled = this.dataset.announcement === 'true';
    this.announcementMode = this.dataset.announcementMode || 'static';

    if (this.isSticky) this.initStickyShadow();
    this.initDrawerBodyLock();
    if (this.announcementEnabled && this.announcementMode === 'rotate') this.initAnnouncementRotate();
  }

  initStickyShadow() {
    if (!this.wrap) return;
    const threshold = 12;

    const apply = () => {
      const y = window.scrollY || 0;
      this.wrap.classList.toggle('is-scrolled', y > threshold);
    };

    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          apply();
          ticking = false;
        });
      },
      { passive: true }
    );

    apply();
  }

  initDrawerBodyLock() {
    const drawer = this.querySelector('header-drawer');
    if (!drawer) return;

    const details = drawer.querySelector('details');
    if (!details) return;

    const sync = () => {
      if (details.open) {
        document.documentElement.classList.add('sa-drawer-open');
        document.body.style.overflow = 'hidden';
      } else {
        document.documentElement.classList.remove('sa-drawer-open');
        document.body.style.overflow = '';
      }
    };

    details.addEventListener('toggle', sync);
    sync();
  }

  initAnnouncementRotate() {
    const slot = this.querySelector('[data-announcement-slot]');
    const dataNode = this.querySelector('[data-announcement-items]');
    if (!slot || !dataNode) return;

    let payload;
    try {
      payload = JSON.parse(dataNode.textContent || '{}');
    } catch (_) {
      return;
    }

    const items = Array.isArray(payload.items) ? payload.items.filter(Boolean) : [];
    const intervalMs = Number(payload.intervalMs || 3500);

    if (items.length <= 1) return;

    let i = 0;
    window.setInterval(() => {
      i = (i + 1) % items.length;
      slot.innerHTML = items[i];
    }, Math.max(2000, intervalMs));
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', SAHeader);
}

// Desktop flyout nav (Impact style)
class DesktopFlyoutNav {
  constructor() {
    this.mql = window.matchMedia('(min-width: 990px)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.enabled = false;
    this.activeTrigger = null;
    this.lastTrigger = null;

    this.handleMqlChange = this.handleMqlChange.bind(this);
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleChildActivate = this.handleChildActivate.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.handleSecondaryBackClick = this.handleSecondaryBackClick.bind(this);
  }

  init() {
    // Only select non-megamenu triggers for old dropdown system
    const allTriggers = Array.from(document.querySelectorAll('[data-desktop-nav-trigger]'));
    this.triggers = allTriggers.filter(t => !t.classList.contains('desktop-nav__trigger--megamenu'));
    this.overlay = document.querySelector('[data-desktop-nav-overlay]');
    this.panels = Array.from(document.querySelectorAll('[data-desktop-nav-panel]'));
    this.stacks = Array.from(document.querySelectorAll('[data-desktop-nav-stack]'));
    this.secondaries = Array.from(document.querySelectorAll('[data-desktop-nav-secondary]'));

    if (!this.triggers.length || !this.panels.length) {
      console.warn('DesktopFlyoutNav: no triggers or panels found');
      return;
    }

    this.mql.addEventListener('change', this.handleMqlChange);
    this.enableIfDesktop(this.mql.matches);
  }

  handleMqlChange(event) {
    this.enableIfDesktop(event.matches);
  }

  enableIfDesktop(isDesktop) {
    if (isDesktop) {
      this.enable();
    } else {
      this.disable();
    }
  }

  enable() {
    if (this.enabled) return;
    this.enabled = true;
    this.bindEvents();
    this.closeAll(true, true);
  }

  disable() {
    if (!this.enabled) return;
    this.unbindEvents();
    this.enabled = false;
    this.closeAll(true, true);
  }

  bindEvents() {
    this.triggers.forEach((trigger) => trigger.addEventListener('click', this.handleTriggerClick));
    if (this.overlay) this.overlay.addEventListener('click', this.handleOverlayClick);
    document.addEventListener('keyup', this.handleKeydown);
    this.bindPanelEvents();
  }

  unbindEvents() {
    this.triggers.forEach((trigger) => trigger.removeEventListener('click', this.handleTriggerClick));
    if (this.overlay) this.overlay.removeEventListener('click', this.handleOverlayClick);
    document.removeEventListener('keyup', this.handleKeydown);
    this.unbindPanelEvents();
  }

  bindPanelEvents() {
    this.panels.forEach((panel) => {
      const closeBtn = panel.querySelector('[data-desktop-nav-close]');
      if (closeBtn) closeBtn.addEventListener('click', this.handleCloseClick);

      const childButtons = panel.querySelectorAll('[data-desktop-nav-child]');
      childButtons.forEach((btn) => {
        btn.addEventListener('click', this.handleChildActivate);
      });
    });

    this.secondaries.forEach((panel) => {
      const closeBtn = panel.querySelector('[data-desktop-nav-close]');
      if (closeBtn) closeBtn.addEventListener('click', this.handleCloseClick);

      const backBtn = panel.querySelector('[data-desktop-nav-back-secondary]');
      if (backBtn) backBtn.addEventListener('click', this.handleSecondaryBackClick);
    });
  }

  unbindPanelEvents() {
    this.panels.forEach((panel) => {
      const closeBtn = panel.querySelector('[data-desktop-nav-close]');
      if (closeBtn) closeBtn.removeEventListener('click', this.handleCloseClick);

      const childButtons = panel.querySelectorAll('[data-desktop-nav-child]');
      childButtons.forEach((btn) => {
        btn.removeEventListener('click', this.handleChildActivate);
      });
    });

    this.secondaries.forEach((panel) => {
      const closeBtn = panel.querySelector('[data-desktop-nav-close]');
      if (closeBtn) closeBtn.removeEventListener('click', this.handleCloseClick);

      const backBtn = panel.querySelector('[data-desktop-nav-back-secondary]');
      if (backBtn) backBtn.removeEventListener('click', this.handleSecondaryBackClick);
    });
  }

  handleTriggerClick(event) {
    if (!this.enabled) return;
    const trigger = event.currentTarget;
    const targetId = trigger.getAttribute('aria-controls');
    const panel = targetId ? document.getElementById(targetId) : null;

    if (!panel) return;

    if (this.activeTrigger === trigger) {
      this.closeAll();
      return;
    }

    this.openPanel(panel, trigger);
  }

  handleOverlayClick() {
    this.closeAll();
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.activeTrigger) {
      this.closeAll();
    }
  }

  handleChildActivate(event) {
    if (!this.enabled) return;
    const btn = event.currentTarget;
    const panel = btn.closest('[data-desktop-nav-panel]');
    if (!panel) return;

    const index = Number(btn.dataset.desktopNavChild || 0);
    const navIndex = panel.dataset.navIndex;
    const hasGroup = this.panelHasGroup(navIndex, index);

    if (!hasGroup) {
      const url = btn.dataset.childUrl || btn.getAttribute('href');
      if (url) {
        window.location.href = url;
        return;
      }
    }

    this.openSecondary(navIndex, index);
  }

  handleSecondaryBackClick(event) {
    if (!this.enabled) return;
    const panel = event.currentTarget.closest('[data-desktop-nav-secondary]');
    if (!panel) return;
    this.resetSecondary(panel);
  }

  handleCloseClick() {
    this.closeAll();
  }

  openPanel(panel, trigger) {
    this.activeTrigger = trigger;
    this.lastTrigger = trigger;
    const navIndex = panel.dataset.navIndex;

    this.triggers.forEach((btn) => btn.setAttribute('aria-expanded', btn === trigger ? 'true' : 'false'));

    this.stacks.forEach((stack) => {
      const isTarget = stack.dataset.desktopNavStack === navIndex;
      stack.hidden = !isTarget;
      stack.classList.toggle('is-active', isTarget);
    });

    this.panels.forEach((p) => {
      const isTarget = p === panel;
      p.hidden = !isTarget;
      p.classList.toggle('is-active', isTarget);
      this.clearChildState(p);
    });

    const secondary = this.getSecondary(navIndex);
    if (secondary) this.resetSecondary(secondary);

    this.showOverlay();
  }

  closeAll(skipFocus = false, force = false) {
    this.activeTrigger = null;
    this.triggers.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    this.stacks.forEach((stack) => {
      stack.classList.remove('is-active');
      stack.hidden = true;
    });

    this.panels.forEach((panel) => {
      panel.classList.remove('is-active');
      panel.hidden = true;
      this.clearChildState(panel);
    });

    this.secondaries.forEach((secondary) => {
      this.resetSecondary(secondary);
    });

    this.hideOverlay();

    if (!skipFocus && this.lastTrigger) {
      this.lastTrigger.focus();
    }

    if (force) this.lastTrigger = null;
  }

  showOverlay() {
    if (this.overlay) {
      this.overlay.hidden = false;
      this.overlay.classList.add('is-active');
      if (this.reducedMotion.matches) {
        this.overlay.classList.add('reduce-motion');
      }
    }
    document.documentElement.classList.add('desktop-nav-open');
    document.body.classList.add('desktop-nav-open');
  }

  hideOverlay() {
    if (this.overlay) {
      this.overlay.classList.remove('is-active');
      this.overlay.classList.remove('reduce-motion');
      this.overlay.hidden = true;
    }
    document.documentElement.classList.remove('desktop-nav-open');
    document.body.classList.remove('desktop-nav-open');
  }

  panelHasGroup(navIndex, index) {
    const secondary = this.getSecondary(navIndex);
    if (!secondary) return false;
    const group = secondary.querySelector(`[data-desktop-nav-group="${index}"]`);
    return Boolean(group);
  }

  clearChildState(panel) {
    const childButtons = panel.querySelectorAll('[data-desktop-nav-child]');
    const groups = panel.querySelectorAll('[data-desktop-nav-group]');
    childButtons.forEach((btn) => btn.classList.remove('is-active'));
    groups.forEach((group) => group.classList.remove('is-active'));
  }

  openSecondary(navIndex, index) {
    const secondary = this.getSecondary(navIndex);
    if (!secondary) return;

    const primary = this.getPrimary(navIndex);
    if (primary) {
      const childButtons = primary.querySelectorAll('[data-desktop-nav-child]');
      childButtons.forEach((btn) => {
        btn.classList.toggle('is-active', Number(btn.dataset.desktopNavChild || 0) === index);
      });
    }

    secondary.hidden = false;
    secondary.querySelectorAll('[data-desktop-nav-group]').forEach((group) => {
      const isMatch = group.dataset.desktopNavGroup === String(index);
      group.hidden = !isMatch;
    });
  }

  resetSecondary(panel) {
    panel.hidden = true;
    panel.querySelectorAll('[data-desktop-nav-group]').forEach((group) => {
      group.hidden = true;
    });
  }

  getSecondary(navIndex) {
    return this.secondaries.find((panel) => panel.dataset.navIndex === String(navIndex));
  }

  getPrimary(navIndex) {
    return this.panels.find((panel) => panel.dataset.navIndex === String(navIndex));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const desktopNav = new DesktopFlyoutNav();
  desktopNav.init();
});


